import {Text} from '@mantine/core';
import {useModals} from '@mantine/modals';
import useApiMutation from 'shared/hooks/use-api-mutation';
import useInfiniteQueryHelpers from 'shared/hooks/use-infinite-query-helpers';
import useQueryHelpers from 'shared/hooks/use-query-helpers';

export interface ApiDynamicButtonProps {
  request: {
    url: string;
    method: 'PATCH' | 'DELETE' | 'POST';
    field?: string;
    errorTitle: string;
    successTitle?: string;
    successMessage?: string;
  };
  button: {
    tooltip: string;
    icon: React.ReactNode;
  };
  confirmButtonText?: string;
  keys: {
    details?: any;
    updateList?: any;
    updateInfiniteList?: any;
    deleteList?: any;
    deleteInfiniteList?: any;
    createInfiniteList?: any;
  };
}

const useButtonState = ({
  request,
  keys,
  confirmButtonText,
}: ApiDynamicButtonProps) => {
  const queryHelpers = useQueryHelpers();
  const infiniteQueryHelpers = useInfiniteQueryHelpers();
  const {openConfirmModal} = useModals();

  const operationMap: Record<string, (data: any) => void> = {
    details: (data: any) => queryHelpers.setData(keys?.details as string, data),
    updateList: (data: any) =>
      queryHelpers.update(keys?.updateList as string, data),
    updateInfiniteList: (data: any) =>
      infiniteQueryHelpers.update(keys?.updateInfiniteList as string, data),
    deleteList: (data: any) =>
      queryHelpers.remove(keys?.deleteList as string, data),
    deleteInfiniteList: (data: any) =>
      infiniteQueryHelpers.remove(keys?.deleteInfiniteList as string, data),
    createInfiniteList: (data: any) =>
      infiniteQueryHelpers.create(keys?.createInfiniteList, data),
  };

  const onSuccess = (data: any) => {
    const executeOperation = (key: string) => {
      const operation = operationMap[key];
      if (operation) {
        operation(data);
      }
    };

    if (keys) {
      Object.keys(keys).forEach(executeOperation);
    }
  };

  const {method, url, errorTitle, successTitle, successMessage, field} =
    request;

  const {mutate, isLoading} = useApiMutation({
    method,
    url,
    errorTitle,
    successTitle,
    successMessage,
    onSuccess,
  });

  const handleRequest = () => {
    if (confirmButtonText)
      return openConfirmModal({
        title: `Remover ${confirmButtonText}`,
        children: <Text>Deseja realmente remover o registro?</Text>,
        labels: {confirm: 'Sim', cancel: 'Não'},
        onConfirm: () => mutate({id: field}),
      });

    mutate({id: field});
  };

  return {
    handleRequest,
    isLoading,
  };
};

export default useButtonState;
